import React from 'react';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { BarChart3 } from 'lucide-react';
import { formatoMonto, useAnalisis } from './useAnalisis';
import { PanelAnalisis } from './PanelAnalisis';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const etiqueta = (m) => `${MESES[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;
const corto = (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : Math.abs(v) >= 1e3 ? `${Math.round(v / 1e3)}k` : v);

// D2 / F3: barras de ingresos y egresos por mes (12 meses), con marca en los meses con alta de CVU o cambio de sponsor.
// La línea "perfil declarado" solo se dibuja si se pasa `perfilDeclarado` (hoy el dato no existe).
export const IngresosEgresos = ({ cuit, casoId, episodioId, desde, hasta, perfilDeclarado }) => {
  const { data, loading, error } = useAnalisis({ cuit, casoId, episodioId, desde, hasta });
  const meses = (data?.mensual || []).map((m) => ({ ...m, perfil: perfilDeclarado || undefined }));
  const marcas = {};
  (data?.marcas || []).forEach((x) => { marcas[x.mes] = [...(marcas[x.mes] || []), x.tipo]; });
  const vacio = data && !meses.some((m) => m.cantidad) ? 'Sin movimientos en los últimos 12 meses' : null;
  return (
    <PanelAnalisis titulo="Ingresos contra egresos por mes" icono={BarChart3} testid="ingresos-egresos"
      loading={loading} error={error} vacio={vacio}>
      <div className="h-64" data-testid="ingresos-egresos-grafico">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={meses} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="mes" tickFormatter={etiqueta} tick={{ fontSize: 11, fill: '#64748b' }} />
            <YAxis tickFormatter={corto} tick={{ fontSize: 11, fill: '#64748b' }} width={48} />
            <Tooltip formatter={(v, n) => [formatoMonto(v), n]} labelFormatter={(m) => `${etiqueta(m)}${marcas[m] ? ` · ${marcas[m].join(', ')}` : ''}`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="ingresos" name="Ingresos" fill="#10b981" radius={[3, 3, 0, 0]} />
            <Bar dataKey="egresos" name="Egresos" fill="#f43f5e" radius={[3, 3, 0, 0]} />
            {perfilDeclarado ? <Line dataKey="perfil" name="Perfil declarado" stroke="#6366f1" strokeDasharray="5 4" dot={false} /> : null}
            {Object.entries(marcas).map(([mes, tipos]) => (
              <ReferenceLine key={mes} x={mes} stroke="#f59e0b" strokeDasharray="2 3"
                label={{ value: tipos.some((t) => t.includes('sponsor')) ? 'Sponsor' : 'CVU', position: 'top', fontSize: 10, fill: '#b45309' }} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {Object.keys(marcas).length > 0 && (
        <p className="mt-2 text-[11px] text-amber-700" data-testid="ingresos-egresos-marcas">
          Marcas: meses con alta de CVU o cambio de sponsor.
        </p>
      )}
    </PanelAnalisis>
  );
};

export default IngresosEgresos;
